import { ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { BaggageKeys } from '../baggageKeys';
import { setBaggageValue } from '../baggage/baggageRegistry';
import { resolvePageId, resolveRouteParams } from '../page/pageResolver';
import { TelemetryApi, TelemetryContext } from './TelemetryContext';
import { useFullStorySession } from '../fullstory/useFullStorySession';

/**
 * Provides the telemetry reporting API. Mount directly inside Router, above the providers
 * that report data -- they push to it via useTelemetry, so it needs only useLocation.
 *
 * Only App.tsx should import this (or the `telemetry` barrel that re-exports it). App
 * providers must import useTelemetry from `telemetry/useTelemetry` instead; importing this
 * module reaches core/routes/Router and cycles back through them.
 */
export default function TelemetryProvider({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();

  // Written during render, not in an effect: React flushes effects child-first, so a page's
  // own mount effect fires its requests before this provider's effect would run.
  setBaggageValue(BaggageKeys.PageId, resolvePageId(location.pathname));

  // Route-derived stand-in until FileDataProvider reports a loaded file.
  setBaggageValue(BaggageKeys.FileId, resolveRouteParams(location.pathname).fileId);

  useFullStorySession();

  // Registry setters are module-level, so this is stable for the life of the app. It must not
  // be rebuilt per render or every useTelemetry() consumer re-renders on each navigation.
  const api = useMemo<TelemetryApi>(() => ({ setTelemetryBaggage: setBaggageValue }), []);

  return <TelemetryContext.Provider value={api}>{children}</TelemetryContext.Provider>;
}
